import { useState } from 'react';
import { Outlet } from 'react-router';
import { Menu, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { usePersistedState, useHotkey, useIsDesktop } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Sidebar, SidebarNav } from './sidebar';
import { Logo, LogoMark } from './logo';
import { CommandPalette } from './command-palette';
import { NotificationsMenu } from './notifications-menu';
import { ThemeToggle } from './theme-toggle';
import { UserMenu } from './user-menu';

/**
 * Armazón de la aplicación: barra lateral + barra superior + contenido.
 *
 * Por debajo de `lg` la barra lateral se convierte en un cajón (Sheet): en una
 * pantalla estrecha, 264 px fijos se comerían la mitad del ancho útil.
 */
export function AppShell() {
  const isDesktop = useIsDesktop();
  const [collapsed, setCollapsed] = usePersistedState('crm.sidebar.collapsed', false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useHotkey('k', () => setPaletteOpen(true), { meta: true });

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-canvas">
      <Sidebar collapsed={collapsed} onToggleCollapsed={() => setCollapsed(!collapsed)} />

      {/* Cajón lateral en pantallas pequeñas. */}
      <Sheet open={drawerOpen && !isDesktop} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[264px] p-0">
          <div className="flex h-topbar shrink-0 items-center border-b border-border px-3">
            <Logo className="h-8" />
          </div>
          <SheetTitle className="sr-only">Navegación principal</SheetTitle>
          <SidebarNav collapsed={false} onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-topbar shrink-0 items-center gap-2 border-b border-border bg-surface-header px-3 sm:px-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label="Abrir el menú"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu aria-hidden="true" />
          </Button>

          <LogoMark className="lg:hidden" />

          {/* El buscador es un botón, no un input: la búsqueda real vive en la
              paleta de comandos, que además navega. */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className={cn(
              'flex h-control-sm min-w-0 flex-1 items-center gap-2 rounded-md border border-border-input bg-surface px-2.5',
              'text-sm text-fg-subtle transition-colors duration-fast',
              'hover:bg-surface-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
              'sm:max-w-md',
            )}
          >
            <Search className="size-4 shrink-0" aria-hidden="true" />
            <span className="truncate">Buscar clientes, tickets…</span>
            <kbd className="ml-auto hidden shrink-0 rounded-xs border border-border px-1.5 py-0.5 font-mono text-2xs text-fg-subtle sm:inline">
              ⌘K
            </kbd>
          </button>

          <div className="ml-auto flex shrink-0 items-center gap-0.5">
            <NotificationsMenu />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <main id="contenido" className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
